import { Queue, QueueItem } from "oj-queue";
import { memoize } from "oj-utils";
import { orderDialog } from "../components/order/orderDialog";
import { purifyRichtext } from "../utils/purifyRichtext";
import { getAsset } from "../utils/utils";
import { getText, getTextFixed, getUrlPrefix } from "./translations";
const queue = new Queue({ concurrent: 1, delay: 200 });
const isLocalHost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
export const base = isLocalHost ? window["SITE_URL"] : `${window.location.origin}/`;
export const nrg_url = `https://www.railpro.online/nl_NL/page/2098/nrg-las-configureren/`;
const contact_urls = {
    "www.railpro.online": `https://www.railpro.online/nl_NL/page/2132/contact/`,
    "test-railpro.cloudsuite.com": `https://www.railpro.online/nl_NL/page/2132/contact/`,
    "railway-systems-shop.voestalpine.com": `https://railway-systems-shop.voestalpine.com/${getUrlPrefix()}/page/2340/ueber-uns/`,
};
export const contact_url = contact_urls[location.host]
    ?? `https://railway-systems-shop.voestalpine.com/${getUrlPrefix()}/page/2340/ueber-uns/`;
export const getImageUrl = (path) => `${base}${path}`;
export const getProductByArticleNumber = memoize(async (artCodeTSNL) => {
    const searchData = await fetch(`${base}${getUrlPrefix()}/api/v1/search/products/?search_term=${encodeURIComponent(artCodeTSNL)}`)
        .then(x => x.json())
        .catch(() => null);
    return searchData?.products?.[0];
}, product => product);
export const getProductStockFromCache = async (artCodeTSNL) => {
    const stock = await fetch(`${window["API_URL"]}umbraco/api/stockapi/stock?stockCode=${encodeURIComponent(artCodeTSNL)}`)
        .then(x => x.json())
        .catch(() => null);
    return stock;
};
export const getProductDetails = memoize(async (artCodeTSNL) => {
    const prod = await getProductByArticleNumber(artCodeTSNL);
    if (!prod)
        throw new Error(`Product with article number ${artCodeTSNL} not found`);
    const url = `${base}${getUrlPrefix()}/api/v1/product/v2/${prod.id}/?features=prices&features=stock&features=images&features=attributes`;
    try {
        const item = new QueueItem(async (url, resolve) => resolve(await fetch(url).then(x => x.json()).catch(() => ({}))), url);
        queue.add(item);
        const data = await item.promise;
        if ("api_error_code" in data)
            throw new Error(`API error ${data.api_error_code}: ${data["message"]}`);
        const product = {
            artCodeTSNL,
            id: data.id ?? prod.id,
            code: data.code ?? prod.code,
            name: data.name ?? data.analytics_name ?? prod.name,
            description: purifyRichtext(data.description ?? data.description2 ?? prod.description ?? prod.description2),
            image: data.picture?.normal ? getImageUrl(data.picture.normal) : undefined,
            thumbnail: data.picture?.mini ? getImageUrl(data.picture.mini) : undefined,
            price: Number(data.prices?.sale ?? ("prices" in prod ? prod.prices.sale : prod.listPrices?.[0]?.value) ?? 0) || undefined,
            properties: [
                ...(data.variant?.name
                    ? [{ name: "Variant", value: `${data.variant.name}${data.variant.attributes ? ` (${data.variant.attributes.map(x => `${x.name}: ${x.value}`).join(", ")})` : ""}` }]
                    : []),
                ...(data.attributes?.flatMap(x => x.properties.map(p => ({ name: p.name, value: p.value })))
                    ?? prod.attributes?.flatMap(x => "properties" in x ? x.properties.map(p => ({ name: p.name, value: p.value })) : [{ name: x.name, value: x.value }]))
            ],
            stock: {
                amount: data.stock?.amount || 0,
                message: data.stock?.message || undefined,
                in_stock: (data.stock?.amount || 0) > 0,
                in_stock_message: getTextFixed(data.stock?.amount ? "IN_STOCK" : "OUT_OF_STOCK")
            },
            link: data.page_link,
            documents: data.assets
                ?.filter(x => x.type === "url")
                .map(x => ({
                title: x.name,
                url: x.asset
            }))
        };
        if (product.stock.amount > 0)
            return product;
        try {
            const cachedStock = await getProductStockFromCache(artCodeTSNL);
            if (!cachedStock || cachedStock.stock === 0)
                return product;
            product.stock.amount = cachedStock.stock;
            product.stock.in_stock = true;
            product.stock.in_stock_message = cachedStock.text || getTextFixed("IN_STOCK");
        }
        catch {
            // ignore errors, return product with stock 0
        }
        return product;
    }
    catch (err) {
        console.error(`Could not fetch details for url ${url}`);
    }
});
export const DEFAULT_IMAGE = getAsset("logo/railway-systems-logo.svg");
const images = {
    nrglassen: getAsset("images/nrglassen.png"),
    strijkregels: getAsset("images/strijkregels.jpg"),
    halveTongbewegingen: getAsset("images/halveTongbewegingen.jpg"),
    puntstukken: getAsset("images/puntstukken.jpg"),
    kruisstukken: getAsset("images/kruisstukken.jpg"),
    // bevestigingsmaterialen: getAsset("images/bevestigingsmaterialen.jpg"),
    // rugplatenEnStoelen: getAsset("images/rugplatenEnStoelen.jpg"),
    // onderlegplaten: getAsset("images/onderlegplaten.jpg"),
};
export const getFullProduct = memoize(async (product) => {
    if (product.artCodeTSNL === "configurator") {
        const copy = { ...product };
        copy.image = images[product.category] ?? copy.image;
        copy.thumbnail = images[product.category] ?? copy.thumbnail;
        return copy;
    }
    const copy = { ...product };
    const img = getProductImage(product);
    copy.image = img.image ?? copy.image;
    copy.thumbnail = img.thumbnail ?? copy.thumbnail;
    copy.name = getProductName(product);
    try {
        const details = await getProductDetails(product.artCodeTSNL);
        const img = getProductImage(details);
        Object.assign(copy, details, {
            image: img.image ?? details.image,
            thumbnail: img.thumbnail ?? details.thumbnail,
            name: getProductName(copy, details),
        });
        return copy;
    }
    catch {
        // ignore errors, return what we have
    }
    return copy;
}, product => product.artCodeTSNL);
export const getProductName = (product, fullProduct) => {
    let name = fullProduct?.name;
    name ??= (product && "name" in product) ? product.name : undefined;
    name ??= (product && "description" in product) ? product.description?.() : undefined;
    name ??= (fullProduct && "description" in fullProduct) ? (typeof fullProduct.description === "function" ? fullProduct.description() : fullProduct.description) : undefined;
    name ??= (product && "category" in product) ? product.category : fullProduct && "category" in fullProduct ? fullProduct?.category : undefined;
    name ??= getTextFixed("LOADING");
    name = getText(name, undefined, name);
    return name;
};
export const getProductImage = (product, fullProduct) => {
    // fall back to category placeholder only when no real image came from the API
    let image = fullProduct?.image ?? product.image ?? images[product.category];
    let thumbnail = fullProduct?.thumbnail ?? product.thumbnail ?? images[product.category];
    return { image, thumbnail };
};
export const placeOrder = async (state) => {
    const articles = new Map();
    for (const art in state.articles) {
        articles.set(art, state.articles[art].quantity);
        for (const x in state.crosssellArticles?.[art] ?? [])
            articles.set(x, (articles.get(x) ?? 0 + (state.crosssellArticles[art][x].quantity ?? 0)));
    }
    const serviceArticles = state.getServicesArticles();
    for (const art in serviceArticles)
        articles.set(art, (articles.get(art) ?? 0) + serviceArticles[art]);
    const d = orderDialog();
    const total = articles.size;
    let current = 0;
    d.update(current, total);
    for (const [artCode, quantity] of articles) {
        if (quantity > 0) {
            try {
                const id = await getProductByArticleNumber(artCode).then(x => x?.id);
                await addToCart(id, quantity);
            }
            catch (err) {
                console.error(`Failed to add product ${artCode} to cart`, err);
            }
            finally {
                current++;
                d.update(current, total);
            }
        }
    }
    location.href = `${base}${getUrlPrefix()}/basket/index/`;
};
export const addToCart = async (productId, quantity) => {
    const url = `${base}${getUrlPrefix()}/xhr/basket/add/`;
    try {
        const fd = new FormData();
        fd.append("product", productId.toString());
        fd.append("con", "None");
        fd.append("thum", "None");
        fd.append("quantity", quantity.toString());
        fd.append("options", JSON.stringify([]));
        await fetch(url, {
            method: "POST",
            body: fd
        });
    }
    catch (err) {
        console.error(`Could not fetch details for url ${url}`);
    }
};
